import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { MeteredUsageAccount, TurnBillingUsage } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { Icon, Seg } from '../ChatAssistant/compact/kit';
import { accountName, int, isTurn, rate3, tierFor, tierRange, usdCents } from './model';
import { Avatar, GhostBtn, Note, StatStrip, THeadRow, Table, type ConfirmSpec } from './ui';
import { ROW_LINE } from './styles';
import type { PaymentsData } from './usePaymentsData';
import { pct } from './volume';
import { Spinner } from '../Spinner';

type Mode = 'sub' | 'od';
type Period = 'hour' | 'today' | '7d' | 'month' | 'all' | 'custom';
const SUB_COLS = 'minmax(180px,1.4fr) 120px 120px 130px 190px';
const OD_COLS = 'minmax(180px,1.4fr) 150px 110px 120px 130px';

function rangeFor(p: Period, from: string, to: string): { start?: string; end?: string } {
  const now = Date.now();
  if (p === 'hour') return { start: new Date(now - 3600e3).toISOString() };
  if (p === 'today') { const d = new Date(); d.setHours(0, 0, 0, 0); return { start: d.toISOString() }; }
  if (p === '7d') return { start: new Date(now - 7 * 86400e3).toISOString() };
  if (p === 'month') return { start: new Date(now - 30 * 86400e3).toISOString() };
  if (p === 'custom') {
    const out: { start?: string; end?: string } = {};
    if (from) out.start = new Date(from + 'T00:00:00').toISOString();
    if (to) { const e = new Date(to + 'T00:00:00'); e.setDate(e.getDate() + 1); out.end = e.toISOString(); }
    return out;
  }
  return {};
}

const dateInput = { height: 30, boxSizing: 'border-box' as const, padding: '0 6px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: 'var(--om-body)', font: f(400, 12), colorScheme: 'dark' as const };

export function UsageTab({ data, initialMode, confirm, setError }: { data: PaymentsData; initialMode: Mode; confirm: (s: ConfirmSpec) => void; setError: (m: string) => void }) {
  const { t } = useTranslation('paymentsCompact');
  const { isSuperAdmin } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [period, setPeriod] = useState<Period>('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [od, setOd] = useState<TurnBillingUsage | null>(null);
  const [odLoading, setOdLoading] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [sweepNote, setSweepNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const metered = data.metered;
  const subA = (metered?.accounts ?? []).filter((a) => a.billing_mode === 'metered');
  const odCount = (data.summary?.accounts ?? []).filter(isTurn).length;
  // Overage settles at the list rate less the Payer's Volume discount (ADR 0038).
  const vd = metered?.volume_discount;
  const rates = [...new Set(subA.map((a) => a.rate_usd).filter((r) => r > 0))].sort((x, y) => x - y);
  const rateValue = rates.length ? (rates.length === 1 ? '$' + rates[0]!.toFixed(5) : '$' + rates[0]!.toFixed(5) + '–' + rates[rates.length - 1]!.toFixed(5)) : '—';
  const rateSub = vd && vd.bps > 0
    ? vd.source === 'override' ? t('volume.rateSpecial', { pct: pct(vd.bps) }) : t('volume.rateOff', { pct: pct(vd.bps), count: vd.paid_count })
    : t('volume.rateList');

  useEffect(() => {
    if (mode !== 'od') return;
    let alive = true;
    setOdLoading(true);
    api.getTurnBillingUsage(rangeFor(period, from, to))
      .then((r) => { if (alive) setOd(r); })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : t('errors.load')); })
      .finally(() => { if (alive) setOdLoading(false); });
    return () => { alive = false; };
  }, [mode, period, from, to, tick, setError, t]);

  const setCap = (a: MeteredUsageAccount, m: 'continue' | 'hard_stop') => {
    if (a.overage_mode === m) return;
    const run = async () => {
      setBusy(a.account_id);
      try {
        await api.setOverageCap({ account_id: a.account_id, overage_mode: m });
        await data.reloadMetered();
      } catch (e) {
        setError(e instanceof Error ? e.message : t('errors.cap'));
      } finally {
        setBusy(null);
      }
    };
    if (m === 'continue') confirm({ title: t('confirm.capOffTitle'), body: t('confirm.capOffBody', { name: accountName(a) }), confirm: t('confirm.capOff'), run });
    else void run();
  };

  const sweep = async () => {
    setSweeping(true);
    setSweepNote('');
    try {
      if (mode === 'od') {
        const r = await api.runTurnBillingSweep();
        setSweepNote(t('usage.swept', { accounts: r.accounts, amount: usdCents(r.charged_cents) }));
        setTick((x) => x + 1);
      } else {
        const r = await api.runMeteredSweep();
        const charged = typeof r.charged_cents === 'number' ? r.charged_cents : 0;
        const accounts = typeof r.accounts === 'number' ? r.accounts : 0;
        setSweepNote(t('usage.swept', { accounts, amount: usdCents(charged) }));
        await data.reloadMetered();
      }
    } catch (e) {
      setSweepNote(e instanceof Error ? e.message : t('usage.sweepFailed'));
    } finally {
      setSweeping(false);
    }
  };

  const getPrice = async (accountId: string) => {
    setBusy(accountId);
    try {
      const res = await api.refreshAccountPrice(accountId);
      if (!res.priced) setError(t('price.unpricedBody'));
      setTick((x) => x + 1);
    } catch (e) {
      const raw = e instanceof Error ? e.message : '';
      setError(raw === 'REQUEST_TIMEOUT' ? t('price.timeout') : raw || t('price.failed'));
    } finally {
      setBusy(null);
    }
  };

  const ladder = od?.rate_ladder ?? data.turn30?.rate_ladder ?? [];
  const under = (v: string) => t('usage.under', { v });
  const odAccounts = od?.accounts ?? [];
  const avgRate = od && od.total_responses > 0 ? od.total_charged_cents / 100 / od.total_responses : null;

  const sweepBtn = isSuperAdmin ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {sweepNote && <span style={{ font: f(400, 11.5), color: C.muted }}>{sweepNote}</span>}
      <GhostBtn icon="refresh-cw" onClick={() => void sweep()} disabled={sweeping} height={28} title={t('usage.sweepTip')}>
        {sweeping ? t('usage.sweeping') : t('usage.sweep')}
      </GhostBtn>
    </div>
  ) : null;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Seg<Mode>
          size="lg"
          value={mode}
          onChange={setMode}
          options={[
            { id: 'sub', label: t('plan.assist.name'), n: subA.length },
            { id: 'od', label: t('plan.od.name'), n: odCount },
          ]}
        />
        <span style={{ font: f(400, 12), color: C.sub, flex: '1 1 260px', minWidth: 0 }}>{mode === 'od' ? t('usage.odDesc') : t('usage.subDesc')}</span>
        {mode === 'od' && (
          <>
            <Seg<Period>
              value={period}
              onChange={setPeriod}
              options={(['hour', 'today', '7d', 'month', 'all'] as const).map((p) => ({ id: p, label: t(`usage.period.${p}`) }))}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPeriod('custom'); }} style={dateInput} />
              <span style={{ color: C.muted, fontSize: 12 }}>–</span>
              <input type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPeriod('custom'); }} style={dateInput} />
            </div>
          </>
        )}
      </div>

      {mode === 'sub' ? (
        <>
          <StatStrip
            items={[
              { label: t('usage.sub.included'), value: metered ? int(metered.total_included_turns) : '—', sub: t('usage.sub.includedSub') },
              { label: t('usage.sub.used'), value: metered ? int(metered.total_used_this_period) : '—', sub: t('accounts.count', { count: subA.length }), accent: true },
              { label: t('usage.sub.overage'), value: metered ? usdCents(metered.total_overage_charged_cents) : '—', sub: t('usage.sub.overageSub') },
              { label: t('volume.rate'), value: metered ? rateValue : '—', sub: rateSub },
            ]}
          />
          {sweepBtn && <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{sweepBtn}</div>}
          <Table minWidth={820}>
            <THeadRow cols={SUB_COLS}>
              <span>{t('accounts.col.account')}</span>
              <span style={{ textAlign: 'right' }}>{t('usage.sub.colIncluded')}</span>
              <span style={{ textAlign: 'right' }}>{t('usage.sub.colUsed')}</span>
              <span style={{ textAlign: 'right' }}>{t('usage.sub.colOverage')}</span>
              <span style={{ textAlign: 'right' }} title={t('accounts.col.capTip')}>{t('accounts.col.cap')}</span>
            </THeadRow>
            {!metered ? (
              <Empty><Spinner size={18} label={t('loading')} /></Empty>
            ) : subA.length === 0 ? (
              <Empty>{t('usage.sub.empty')}</Empty>
            ) : subA.map((a, i) => {
              const over = Math.max(0, a.used_this_period - a.included_turns);
              const pct = a.included_turns > 0 ? Math.round((a.used_this_period / a.included_turns) * 100) : 0;
              return (
                <Row key={a.account_id} cols={SUB_COLS} first={i === 0} name={accountName(a)} avatar={a.avatar_url} tag={a.suspended ? t('usage.sub.paused') : undefined}>
                  <Num v={a.included_turns > 0 ? int(a.included_turns) : '—'} sub={t('row.perMonth', { price: usdCents(a.price_cents) })} />
                  <Num v={int(a.used_this_period)} sub={pct + '%'} accent={over > 0} />
                  <Num v={over ? usdCents(a.overage_charged_cents) : '—'} sub={over ? t('row.over', { n: int(over) }) : ''} accent={over > 0} faint={!over} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', opacity: busy === a.account_id ? 0.6 : 1 }}>
                    <Seg<'continue' | 'hard_stop'>
                      size="sm"
                      value={a.overage_mode}
                      onChange={(m) => setCap(a, m)}
                      disabled={busy === a.account_id}
                      options={[
                        { id: 'continue', label: t('cap.off'), title: t('cap.offTip') },
                        { id: 'hard_stop', label: t('cap.on'), title: t('cap.onTip') },
                      ]}
                    />
                  </div>
                </Row>
              );
            })}
          </Table>
        </>
      ) : (
        <>
          <StatStrip
            items={[
              { label: t('usage.od.charged'), value: od ? usdCents(od.total_charged_cents) : '—', sub: t('accounts.count', { count: odAccounts.length }), accent: true },
              { label: t('usage.od.responses'), value: od ? int(od.total_responses) : '—', sub: t('usage.od.responsesSub') },
              {
                label: t('usage.od.pending'),
                value: od ? '$' + (od.total_pending_cents / 100).toFixed(3) : '—',
                sub: od && od.total_pending_responses > 0 ? t('usage.od.pendingSub', { count: od.total_pending_responses }) : t('usage.od.pendingNone'),
              },
              { label: t('usage.od.avgRate'), value: avgRate != null ? rate3(avgRate) : '—', sub: t('usage.od.perResponse') },
            ]}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setRateOpen((o) => !o)} className="cpt-hover" style={{ height: 28, padding: '0 8px', display: 'flex', alignItems: 'center', gap: 6, border: 0, borderRadius: 6, background: 'transparent', color: C.purpleLt, font: f(500, 12.5), cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <Icon name="chevron-right" size={13} style={{ transform: rateOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
              {t('usage.od.howRate')}
              <span style={{ font: f(400, 11.5), color: C.muted }}>{t('usage.od.howRateHint')}</span>
            </button>
            <span style={{ flex: 1 }} />
            {sweepBtn}
          </div>
          {rateOpen && ladder.length > 0 && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, maxWidth: 520 }}>
              {ladder.map((tier, i) => {
                const n = odAccounts.filter((a) => a.monthly_balance_usd != null && !a.rate_pinned && tierFor(ladder, a.monthly_balance_usd)?.min_gross === tier.min_gross).length;
                return (
                  <div key={tier.min_gross} style={{ display: 'flex', alignItems: 'center', gap: 12, height: 36, padding: '0 14px', borderTop: i ? ROW_LINE : 0, background: n ? 'color-mix(in srgb, var(--om-accent) 7%, transparent)' : 'transparent' }}>
                    <span style={{ flex: 1, font: f(400, 12.5), color: 'var(--om-body)' }}>{tierRange(tier, under)}</span>
                    <span style={{ font: f(400, 11.5), color: C.muted }}>{n ? t('accounts.count', { count: n }) : ''}</span>
                    <span style={{ width: 110, textAlign: 'right', font: f(600, 12.5), color: n ? C.purplePale : C.text, fontVariantNumeric: 'tabular-nums' }}>{t('usage.od.perResp', { rate: rate3(tier.rate_usd) })}</span>
                  </div>
                );
              })}
            </div>
          )}
          <Table minWidth={820}>
            <THeadRow cols={OD_COLS}>
              <span>{t('accounts.col.account')}</span>
              <span style={{ textAlign: 'right' }}>{t('usage.od.colRate')}</span>
              <span style={{ textAlign: 'right' }}>{t('usage.od.colResponses')}</span>
              <span style={{ textAlign: 'right' }}>{t('usage.od.colCharged')}</span>
              <span style={{ textAlign: 'right' }}>{t('usage.od.colTier')}</span>
            </THeadRow>
            {odLoading && !od ? (
              <Empty><Spinner size={18} label={t('loading')} /></Empty>
            ) : odAccounts.length === 0 ? (
              <Empty>{t('usage.od.empty')}</Empty>
            ) : odAccounts.map((a, i) => {
              const unpriced = a.monthly_balance_usd == null && !a.rate_pinned;
              const tier = tierFor(ladder, a.monthly_balance_usd);
              const nextRate = !unpriced && a.tier_rate_usd != null && a.rate_usd != null && a.tier_rate_usd !== a.rate_usd ? a.tier_rate_usd : null;
              return (
                <Row key={a.account_id} cols={OD_COLS} first={i === 0} name={accountName(a)} avatar={a.avatar_url} tag={a.suspended ? t('status.walletEmpty') : undefined}>
                  <Num
                    v={a.rate_usd != null ? rate3(a.rate_usd) + (a.rate_pinned ? ' · ' + t('usage.od.pinned') : '') : '—'}
                    sub={unpriced ? t('usage.od.earnUnknown') : nextRate != null ? t('usage.od.nextDay', { rate: rate3(nextRate) }) : t('usage.od.earned', { amount: '$' + int(a.monthly_balance_usd ?? 0) })}
                  />
                  <Num v={int(a.responses_billed)} />
                  <Num v={usdCents(a.amount_charged_cents)} accent />
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {unpriced && a.can_get_price ? (
                      <GhostBtn icon="tag" height={24} onClick={() => void getPrice(a.account_id)} disabled={busy === a.account_id} busy={busy === a.account_id} title={t('row.getPriceTip')}>
                        {t('row.getPrice')}
                      </GhostBtn>
                    ) : (
                      <span style={{ font: f(500, 12), color: C.sub, whiteSpace: 'nowrap' }}>{a.rate_pinned ? t('usage.od.pinnedTier') : tier ? tierRange(tier, under) : '—'}</span>
                    )}
                  </div>
                </Row>
              );
            })}
          </Table>
          {od && od.accounts.some((a) => a.monthly_balance_usd == null && !a.rate_pinned) && (
            <Note>{t('usage.od.lowestNote', { rate: rate3(od.turn_rate_usd) })}</Note>
          )}
        </>
      )}
    </>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div style={{ padding: '24px 14px', font: f(400, 12.5), color: C.muted }}>{children}</div>;
}

function Row({ cols, first, name, avatar, tag, children }: { cols: string; first: boolean; name: string; avatar?: string | null; tag?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, alignItems: 'center', minHeight: 46, padding: '6px 14px', borderTop: first ? 0 : ROW_LINE }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
        <Avatar name={name} src={avatar} size={26} />
        <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span title={name} style={{ font: f(500, 13), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
          {tag && <span style={{ font: f(400, 11), color: C.purplePale }}>{tag}</span>}
        </span>
      </div>
      {children}
    </div>
  );
}

function Num({ v, sub, accent, faint }: { v: string; sub?: string; accent?: boolean; faint?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
      <span style={{ font: f(500, 12.5), color: faint ? C.faint : accent ? C.purplePale : C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{v}</span>
      {sub ? <span style={{ font: f(400, 11), color: C.muted, whiteSpace: 'nowrap' }}>{sub}</span> : null}
    </div>
  );
}
